/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import AdminLayout from '../../components/AdminLayout';
import adminApi from '../../services/adminApi';
import './EvaluationDashboard.css';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com';

// Default test suites (fallback) - includes SkillBot, Roadmap, and Profile Analysis
const DEFAULT_SUITES = [
  // SkillBot Suites
  { id: 'skillbot-wms', name: 'SkillBot WMS', component: 'skillbot', vertical: 'WMS', testCount: 10, enabled: true },
  { id: 'skillbot-iseries', name: 'SkillBot iSeries', component: 'skillbot', vertical: 'iSeries', testCount: 10, enabled: true },
  { id: 'skillbot-civil', name: 'SkillBot Civil Engineering', component: 'skillbot', vertical: 'Civil', testCount: 10, enabled: true },
  { id: 'skillbot-stitching', name: 'SkillBot Stitching', component: 'skillbot', vertical: 'Stitching', testCount: 10, enabled: true },
  { id: 'skillbot-erpnext', name: 'SkillBot ERPNext', component: 'skillbot', vertical: 'ERPNext', testCount: 10, enabled: true },
  // Roadmap Suites
  { id: 'roadmap-wms', name: 'Roadmap WMS', component: 'roadmap', vertical: 'WMS', testCount: 3, enabled: true },
  { id: 'roadmap-iseries', name: 'Roadmap iSeries', component: 'roadmap', vertical: 'iSeries', testCount: 3, enabled: true },
  { id: 'roadmap-civil', name: 'Roadmap Civil', component: 'roadmap', vertical: 'Civil', testCount: 2, enabled: true },
  { id: 'roadmap-stitching', name: 'Roadmap Stitching', component: 'roadmap', vertical: 'Stitching', testCount: 2, enabled: true },
  { id: 'roadmap-erpnext', name: 'Roadmap ERPNext', component: 'roadmap', vertical: 'ERPNext', testCount: 2, enabled: true },
  // Profile Analysis Suites
  { id: 'profile-wms', name: 'Profile WMS', component: 'profile', vertical: 'WMS', testCount: 3, enabled: true },
  { id: 'profile-iseries', name: 'Profile iSeries', component: 'profile', vertical: 'iSeries', testCount: 3, enabled: true },
  { id: 'profile-civil', name: 'Profile Civil', component: 'profile', vertical: 'Civil', testCount: 2, enabled: true },
  { id: 'profile-stitching', name: 'Profile Stitching', component: 'profile', vertical: 'Stitching', testCount: 2, enabled: true },
  { id: 'profile-erpnext', name: 'Profile ERPNext', component: 'profile', vertical: 'ERPNext', testCount: 2, enabled: true },
  // SkillCode Suites
  { id: 'skillbot-skillcode', name: 'SkillBot SkillCode', component: 'skillbot', vertical: 'SkillCode', testCount: 10, enabled: true },
  { id: 'roadmap-skillcode', name: 'Roadmap SkillCode', component: 'roadmap', vertical: 'SkillCode', testCount: 2, enabled: true },
  { id: 'profile-skillcode', name: 'Profile SkillCode', component: 'profile', vertical: 'SkillCode', testCount: 2, enabled: true }
];

// Default evaluators
const DEFAULT_EVALUATORS = [
  { id: 'llm-judge', name: 'LLM Judge', weight: 30, description: 'Uses Claude to evaluate response quality, relevance, and accuracy' },
  { id: 'ground-truth', name: 'Ground Truth', weight: 25, description: 'Compares against expected answers using semantic matching' },
  { id: 'semantic', name: 'Semantic Similarity', weight: 15, description: 'Measures embedding-based similarity to expected output' },
  { id: 'rubric', name: 'Rubric Scorer', weight: 10, description: 'Scores against predefined criteria and checkpoints' },
  { id: 'hallucination', name: 'Hallucination Detector', weight: 10, description: 'Identifies fabricated information or false claims' },
  { id: 'format', name: 'Format Validator', weight: 10, description: 'Checks response structure, length, and formatting' }
];

// Track colors - consistent across the app
const TRACK_COLORS = {
  'WMS': { bg: '#dbeafe', text: '#1d4ed8', border: '#3b82f6' },
  'iSeries': { bg: '#ede9fe', text: '#6d28d9', border: '#8b5cf6' },
  'Civil': { bg: '#fef3c7', text: '#b45309', border: '#f59e0b' },
  'Stitching': { bg: '#fce7f3', text: '#be185d', border: '#ec4899' },
  'SkillCode': { bg: '#f3e8ff', text: '#7c3aed', border: '#a78bfa' }
};

// LLM Judge criteria labels (human-readable)
const CRITERIA_LABELS = {
  accuracy: 'Accuracy',
  relevance: 'Relevance',
  domain_expertise: 'Domain Expertise',
  helpfulness: 'Helpfulness',
  clarity: 'Clarity',
  safety: 'Safety',
  responseRelevance: 'Response Relevance',
  verticalAccuracy: 'Vertical Accuracy',
  conversationFlow: 'Conversation Flow',
  skillGapAnalysis: 'Skill Gap Analysis',
  learningPathStructure: 'Learning Path Structure',
  experienceExtraction: 'Experience Extraction',
  skillsIdentification: 'Skills Identification',
  levelDetection: 'Level Detection',
  verticalClassification: 'Vertical Classification'
};

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

const getToken = () => {
  return localStorage.getItem('adminAccessToken') || localStorage.getItem('adminToken');
};

const formatVertical = (vertical) => {
  if (!vertical) return '';
  const v = vertical.toUpperCase();
  switch (v) {
    case 'ISERIES': return 'iSeries';
    case 'CIVIL': return 'Civil';
    case 'STITCHING': return 'Stitching';
    case 'WMS': return 'WMS';
    case 'SKILLCODE': return 'SkillCode';
    default: return vertical;
  }
};

const formatSuiteName = (name) => {
  if (!name) return '';
  return name.replace(/\s*Test\s*Suite\s*/gi, '').replace(/\s+/g, ' ').trim();
};

const extractComponentName = (suiteName) => {
  if (!suiteName) return 'Unknown';
  const name = suiteName.toLowerCase();
  if (name.includes('roadmap')) return 'Roadmap';
  if (name.includes('profile')) return 'Profile';
  if (name.includes('skillbot')) return 'SkillBot';
  const firstWord = suiteName.split(/[\s-]/)[0];
  return firstWord.charAt(0).toUpperCase() + firstWord.slice(1).toLowerCase();
};

const formatPassRate = (passed, total) => {
  if (passed === undefined || passed === null || total === undefined || total === null) return '--';
  if (total === 0) return '--';
  const percentage = ((passed / total) * 100).toFixed(0);
  return `${passed}/${total} (${percentage}%)`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return '--';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}m ${secs}s`;
};

const getScoreColor = (score) => {
  if (score === null || score === undefined) return 'score-unknown';
  if (score >= 80) return 'score-excellent';
  if (score >= 60) return 'score-good';
  if (score >= 40) return 'score-fair';
  return 'score-poor';
};

const getScoreEmoji = (score) => {
  if (score === null || score === undefined) return '⚪';
  if (score >= 80) return '🟢';
  if (score >= 60) return '🟡';
  if (score >= 40) return '🟠';
  return '🔴';
};

const getCriteriaLabel = (key) => {
  return CRITERIA_LABELS[key] || key.replace(/_/g, ' ').replace(/([A-Z])/g, ' $1').trim();
};

// ═══════════════════════════════════════════════════════════════════════════
// API CLIENT - Use shared adminApi for consistent auth handling
// ═══════════════════════════════════════════════════════════════════════════

const apiCall = async (endpoint, options = {}) => {
  try {
    const response = await adminApi.get(endpoint);
    return response.data;
  } catch (error) {
    // Handle 2FA required - don't throw, let component handle
    if (error.response?.data?.code === '2FA_REQUIRED') {
      throw new Error('2FA verification required');
    }
    throw new Error(error.response?.data?.error || error.message || 'API error');
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// SCORE BREAKDOWN COMPONENT (NEW)
// ═══════════════════════════════════════════════════════════════════════════

const ScoreBreakdown = ({ scores }) => {
  if (!scores || typeof scores !== 'object' || Object.keys(scores).length === 0) {
    return <span className="no-breakdown">No detailed scores available</span>;
  }

  return (
    <div className="score-breakdown">
      {Object.entries(scores).map(([key, value]) => {
        // Handle different score formats
        const score = typeof value === 'object' ? (value.score ?? value.value ?? value) : value;
        const numericScore = typeof score === 'number' ? score : parseFloat(score);
        
        if (isNaN(numericScore)) return null;
        
        // Normalize to 0-100 scale if it appears to be 0-5 scale
        const normalizedScore = numericScore <= 5 ? numericScore * 20 : numericScore;
        
        return (
          <div key={key} className="score-item">
            <span className="score-label">{getCriteriaLabel(key)}</span>
            <div className="score-bar-container">
              <div 
                className={`score-bar ${getScoreColor(normalizedScore)}`}
                style={{ width: `${Math.min(100, normalizedScore)}%` }}
              />
            </div>
            <span className={`score-value ${getScoreColor(normalizedScore)}`}>
              {numericScore <= 5 ? `${numericScore}/5` : `${Math.round(normalizedScore)}`}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// STRENGTHS & ISSUES COMPONENT (NEW)
// ═══════════════════════════════════════════════════════════════════════════

const StrengthsAndIssues = ({ strengths, issues }) => {
  const hasStrengths = strengths && Array.isArray(strengths) && strengths.length > 0;
  const hasIssues = issues && Array.isArray(issues) && issues.length > 0;
  
  if (!hasStrengths && !hasIssues) return null;
  
  return (
    <div className="strengths-issues-container">
      {hasStrengths && (
        <div className="detail-section strengths-section">
          <h4>✅ Strengths</h4>
          <ul className="strengths-list">
            {strengths.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      {hasIssues && (
        <div className="detail-section issues-section">
          <h4>⚠️ Issues</h4>
          <ul className="issues-list">
            {issues.map((issue, i) => <li key={i}>{issue}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TEST RESULT DETAIL MODAL COMPONENT (ENHANCED)
// ═══════════════════════════════════════════════════════════════════════════

const TestResultModal = ({ run, onClose }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        //const response = await apiCall(`/api/admin/evaluations/runs/${run._id || run.id}/results`);
        const response = await adminApi.get(`/api/admin/evaluations/runs/${run._id || run.id}/results`);

        setResults(response.data?.results || response.results || response.data || []);
      } catch (err) {
        console.error('Failed to fetch results:', err);
        setError(err.message);
        // Try to use embedded results if available
        if (run.results && Array.isArray(run.results)) {
          setResults(run.results);
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchResults();
  }, [run]);

  const toggleRow = (idx) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(idx)) {
      newExpanded.delete(idx);
    } else {
      newExpanded.add(idx);
    }
    setExpandedRows(newExpanded);
  };

  // Format component name for display
  const getComponentDisplay = () => {
    const suiteName = (run.suiteName || run.suite || '').toLowerCase();
    if (suiteName.includes('roadmap')) return '🗺️ Roadmap';
    if (suiteName.includes('profile')) return '👤 Profile';
    return '🤖 SkillBot';
  };

  // Determine pass/fail status
  const passed = run.passed ?? run.summary?.testsPassed ?? 0;
  const failed = run.failed ?? run.summary?.testsFailed ?? 0;
  const total = run.total ?? run.totalTests ?? (passed + failed) ?? results.length;
  const passRateStr = total > 0 ? `${passed}/${total} (${((passed/total)*100).toFixed(0)}%)` : '--';

  // Extract scores from result (handles multiple formats)
  const getResultScores = (result) => {
    // Try different locations where scores might be stored
    if (result.evaluations && Object.keys(result.evaluations).length > 0) {
      return result.evaluations;
    }
    if (result.scores && Object.keys(result.scores).length > 0) {
      return result.scores;
    }
    if (result.details && typeof result.details === 'object') {
      // Check if details contains score-like values
      const hasScores = Object.values(result.details).some(v => 
        typeof v === 'number' || (typeof v === 'object' && v !== null && 'score' in v)
      );
      if (hasScores) return result.details;
    }
    if (result.detailedScores && Object.keys(result.detailedScores).length > 0) {
      return result.detailedScores;
    }
    return null;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="result-modal result-modal-large" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <span className="modal-icon">{getComponentDisplay().split(' ')[0]}</span>
            <span>{formatSuiteName(run.suiteName || run.suite)}</span>
            <span className={`vertical-badge vertical-${(run.vertical || '').toLowerCase()}`}>
              {formatVertical(run.vertical)}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-summary">
          <div className="summary-stat">
            <div className="stat-label">Overall Score</div>
            <div className={`stat-value ${getScoreColor(run.score)}`}>
              {run.score?.toFixed(1) || '--'}
            </div>
          </div>
          <div className="summary-stat">
            <div className="stat-label">Pass Rate</div>
            <div className="stat-value">{passRateStr}</div>
          </div>
          <div className="summary-stat">
            <div className="stat-label">Duration</div>
            <div className="stat-value">{formatDuration(run.duration)}</div>
          </div>
          <div className="summary-stat">
            <div className="stat-label">Evaluated By</div>
            <div className="stat-value evaluator-badge">
              {run.evaluatedBy || 'LLM Judge'}
            </div>
          </div>
        </div>

        <div className="modal-body">
          {run.error && (
            <div className="error-box">
              <strong>Error:</strong> {run.error}
            </div>
          )}
          
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading test results...</p>
            </div>
          ) : error && results.length === 0 ? (
            <div className="error-container">
              <p>Could not load detailed results: {error}</p>
            </div>
          ) : results.length === 0 ? (
            <div className="empty-results">
              <p>No detailed test results available for this run.</p>
            </div>
          ) : (
            <div className="test-results-list">
              <p className="results-hint">💡 Click on a row to see detailed analysis</p>
              <table className="results-table">
                <thead>
                  <tr>
                    <th style={{width: '40px'}}></th>
                    <th>Status</th>
                    <th>Test Case</th>
                    <th>Score</th>
                    <th>Evaluator</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, idx) => {
                    const isExpanded = expandedRows.has(idx);
                    const scores = getResultScores(result);
                    const hasDetails = scores || result.strengths || result.issues || result.summary || result.response;
                    
                    return (
                      <React.Fragment key={idx}>
                        <tr 
                          className={`result-row ${result.passed ? 'test-passed' : 'test-failed'} ${hasDetails ? 'clickable' : ''}`}
                          onClick={() => hasDetails && toggleRow(idx)}
                        >
                          <td className="expand-cell">
                            {hasDetails && (
                              <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>
                                ▶
                              </span>
                            )}
                          </td>
                          <td className="result-status">
                            {result.passed ? (
                              <span className="status-icon passed">✓</span>
                            ) : (
                              <span className="status-icon failed">✗</span>
                            )}
                          </td>
                          <td className="result-input">
                            <div className="input-title">{result.testName || `Test ${idx + 1}`}</div>
                            <div className="input-text">
                              {result.input || result.question || result.description || 'N/A'}
                            </div>
                          </td>
                          <td className={`result-score ${getScoreColor(result.score || result.overallScore)}`}>
                            {getScoreEmoji(result.score || result.overallScore)} {(result.score || result.overallScore)?.toFixed(1) || '--'}
                          </td>
                          <td className="result-evaluator">
                            <span className={`evaluator-tag ${result.evaluatedBy === 'llm-judge' ? 'llm' : 'heuristics'}`}>
                              {result.evaluatedBy === 'llm-judge' ? '🤖 LLM' : '📏 Heuristics'}
                            </span>
                          </td>
                        </tr>
                        
                        {/* Expanded Details Row */}
                        {isExpanded && hasDetails && (
                          <tr className="result-details-row">
                            <td colSpan="5">
                              <div className="result-details">
                                {/* Score Breakdown */}
                                {scores && (
                                  <div className="detail-section">
                                    <h4>📊 Score Breakdown</h4>
                                    <ScoreBreakdown scores={scores} />
                                  </div>
                                )}
                                
                                {/* Strengths & Issues */}
                                <StrengthsAndIssues 
                                  strengths={result.strengths} 
                                  issues={result.issues} 
                                />
                                
                                {/* Summary */}
                                {result.summary && (
                                  <div className="detail-section">
                                    <h4>📝 Summary</h4>
                                    <p className="summary-text">{result.summary}</p>
                                  </div>
                                )}
                                
                                {/* Response Preview */}
                                {result.response && (
                                  <div className="detail-section">
                                    <h4>💬 Response Preview</h4>
                                    <div className="response-text">
                                      {result.response.substring(0, 500)}
                                      {result.response.length > 500 && '...'}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// SUITE CARD COMPONENT (for SkillBot, Roadmap, Profile)
// ═══════════════════════════════════════════════════════════════════════════

const SuiteCard = ({ suite, onRunTest, isRunning, latestScore, latestRun }) => {
  const componentType = suite.component || 'skillbot';
  const icon = componentType === 'roadmap' ? '🗺️' : componentType === 'profile' ? '👤' : '🤖';
  const vertical = formatVertical(suite.vertical);
  
  // Get score from suite or passed-in latestScore
  const score = latestScore ?? suite.lastScore;
  const lastRunDate = latestRun ?? suite.lastRun;
  const totalRuns = suite.totalRuns || 0;
  
  // Get track colors
  const trackColor = TRACK_COLORS[vertical] || { bg: '#f3f4f6', text: '#374151', border: '#9ca3af' };
  
  return (
    <div className={`suite-card vertical-${(suite.vertical || '').toLowerCase()} ${isRunning ? 'running' : ''}`}>
      {/* Header with icon and track name in colored background */}
      <div className="suite-header-new" style={{ backgroundColor: trackColor.bg }}>
        <span className="suite-icon">{icon}</span>
        <span className="suite-track-name" style={{ color: trackColor.text }}>{vertical}</span>
      </div>
      
      <div className="suite-stats">
        <div className="stat">
          <span className="stat-label">Tests</span>
          <span className="stat-value">dynamic</span>
        </div>
        <div className="stat">
          <span className="stat-label">Last Score</span>
          <span className={`stat-value ${getScoreColor(score)}`}>
            {score?.toFixed(0) || '--'}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Pass Rate</span>
          <span className="stat-value">
            {suite.passRate || (suite.passed !== undefined && suite.total ? formatPassRate(suite.passed, suite.total) : '--')}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Total Runs</span>
          <span className="stat-value">{totalRuns}</span>
        </div>
      </div>
      
      {/* Footer - Fixed layout with proper spacing */}
      <div className="suite-footer-new">
        <span className="last-run">
          Last Run: {lastRunDate ? formatDate(lastRunDate) : 'Never'}
        </span>
        <button
          className={`btn btn-primary ${isRunning ? 'btn-loading' : ''}`}
          onClick={() => onRunTest(suite)}
          disabled={isRunning}
        >
          {isRunning ? '⏳ Running...' : '▶ Run Evaluation'}
        </button>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MULTI-TREND CHART COMPONENT (Interactive with tooltips)
// ═══════════════════════════════════════════════════════════════════════════

const MultiTrendChart = ({ data }) => {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [hiddenVerticals, setHiddenVerticals] = useState(new Set());

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="trend-chart-empty">
        <p>No trend data available</p>
      </div>
    );
  }

  const chartWidth = 700;
  const chartHeight = 200;
  const padding = { top: 20, right: 140, bottom: 40, left: 50 };

  // Colors matching track colors
  const colors = {
    'WMS': '#3b82f6',
    'iSeries': '#8b5cf6',
    'Civil': '#f59e0b',
    'Stitching': '#ec4899',
    'Overall': '#6366f1'
  };

  const getColor = (vertical) => colors[vertical] || '#6b7280';
  const getDisplayName = (vertical) => vertical === 'iSeries' ? 'iSeries' : vertical;

  // Get valid data entries (excluding hidden)
  const allData = Object.entries(data).filter(([_, runs]) => runs && runs.length > 0);
  const validData = allData.filter(([vertical]) => !hiddenVerticals.has(vertical));

  if (allData.length === 0) {
    return (
      <div className="trend-chart-empty">
        <p>No trend data available</p>
      </div>
    );
  }

  // Toggle vertical visibility
  const toggleVertical = (vertical) => {
    const newHidden = new Set(hiddenVerticals);
    if (newHidden.has(vertical)) {
      newHidden.delete(vertical);
    } else {
      newHidden.add(vertical);
    }
    setHiddenVerticals(newHidden);
  };

  // Calculate scale
  const allScores = validData.flatMap(([_, runs]) => runs.map(r => r.score)).filter(s => s !== null && s !== undefined);
  const minScore = allScores.length > 0 ? Math.max(0, Math.min(...allScores) - 10) : 0;
  const maxScore = allScores.length > 0 ? Math.min(100, Math.max(...allScores) + 10) : 100;
  const range = maxScore - minScore || 1;

  return (
    <div className="trend-chart interactive">
      <svg width={chartWidth} height={chartHeight + padding.top + padding.bottom}>
        {/* Y-axis labels */}
        {[0, 25, 50, 75, 100].map(pct => {
          const y = padding.top + chartHeight - ((pct - minScore) / range) * chartHeight;
          if (y < padding.top || y > padding.top + chartHeight) return null;
          return (
            <g key={pct}>
              <line x1={padding.left} y1={y} x2={chartWidth - padding.right} y2={y} stroke="#e5e7eb" strokeDasharray="4,4" />
              <text x={padding.left - 10} y={y + 4} textAnchor="end" fontSize="10" fill="#9ca3af">{pct}</text>
            </g>
          );
        })}

        {/* Lines for each vertical */}
        {validData.map(([vertical, runs]) => {
          const validRuns = runs.filter(r => r.score !== null && r.score !== undefined && !isNaN(r.score));
          if (validRuns.length === 0) return null;
          
          const points = validRuns.map((r, i) => ({
            x: padding.left + (i / (Math.max(validRuns.length - 1, 1))) * (chartWidth - padding.left - padding.right),
            y: padding.top + chartHeight - ((r.score - minScore) / range) * chartHeight,
            ...r
          }));

          const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
          const color = getColor(vertical);

          return (
            <g key={vertical}>
              <path d={linePath} fill="none" stroke={color} strokeWidth="2" />
              {points.map((p, i) => (
                <circle
                  key={i}
                  cx={p.x}
                  cy={p.y}
                  r={hoveredPoint?.vertical === vertical && hoveredPoint?.index === i ? 6 : 4}
                  fill={color}
                  stroke="white"
                  strokeWidth="2"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredPoint({ vertical, index: i, ...p })}
                  onMouseLeave={() => setHoveredPoint(null)}
                />
              ))}
            </g>
          );
        })}

        {/* Legend */}
        {allData.map(([vertical], i) => {
          const isHidden = hiddenVerticals.has(vertical);
          return (
            <g
              key={vertical}
              transform={`translate(${chartWidth - padding.right + 20}, ${padding.top + i * 25})`}
              style={{ cursor: 'pointer' }}
              onClick={() => toggleVertical(vertical)}
            >
              <rect width="14" height="14" fill={isHidden ? '#e5e7eb' : getColor(vertical)} rx="2" />
              <text x="20" y="11" fontSize="12" fill={isHidden ? '#9ca3af' : '#374151'}>
                {getDisplayName(vertical)}
              </text>
            </g>
          );
        })}

        {/* Tooltip */}
        {hoveredPoint && (
          <g transform={`translate(${hoveredPoint.x}, ${hoveredPoint.y - 45})`}>
            <rect x="-50" y="-5" width="100" height="40" fill="white" stroke="#e5e7eb" rx="4" />
            <text x="0" y="10" textAnchor="middle" fontSize="11" fontWeight="600" fill="#374151">
              {hoveredPoint.vertical}: {hoveredPoint.score?.toFixed(1)}
            </text>
            <text x="0" y="26" textAnchor="middle" fontSize="9" fill="#6b7280">
              {new Date(hoveredPoint.date).toLocaleDateString()}
            </text>
          </g>
        )}
      </svg>
      <p className="chart-hint">Click legend items to show/hide tracks</p>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DASHBOARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

const EvaluationDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, sessionTimeRemaining } = useAdminAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [suites, setSuites] = useState(DEFAULT_SUITES);
  const [evaluators] = useState(DEFAULT_EVALUATORS);
  const [recentRuns, setRecentRuns] = useState([]);
  const [runningTests, setRunningTests] = useState({});
  const [selectedRun, setSelectedRun] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // Component scores for top cards
  const [componentScores, setComponentScores] = useState({
    skillbot: {},
    roadmap: {},
    profile: {}
  });

  // Format session time
  const formatSessionTime = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  // Load data on mount
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch recent runs
      // const runsResponse = await apiCall('/api/admin/evaluations/runs?limit=50');
       const runsResponse = await adminApi.get('/api/admin/evaluations/runs?limit=50');
      
      // Debug: log the response structure
      console.log('[EvalDashboard] runsResponse:', runsResponse);
      console.log('[EvalDashboard] runsResponse.data:', runsResponse.data);
      
      // Handle all possible response structures
      let runs = [];
      if (Array.isArray(runsResponse)) {
        runs = runsResponse;
      } else if (Array.isArray(runsResponse.data)) {
        runs = runsResponse.data;
      } else if (runsResponse.data?.data && Array.isArray(runsResponse.data.data)) {
        runs = runsResponse.data.data;
      } else if (runsResponse.data?.runs && Array.isArray(runsResponse.data.runs)) {
        runs = runsResponse.data.runs;
      } else if (runsResponse.runs && Array.isArray(runsResponse.runs)) {
        runs = runsResponse.runs;
      } else {
        console.warn('[EvalDashboard] Unexpected response structure:', runsResponse);
      }
      
      setRecentRuns(runs);
      
      
      // Calculate component scores from runs
      const scores = { skillbot: {}, roadmap: {}, profile: {} };
      runs.forEach(run => {
        if (run.score === null || run.score === undefined) return;
        const component = extractComponentName(run.suiteName || '').toLowerCase();
        const vertical = (run.vertical || '').toLowerCase();
        if (component && vertical && ['skillbot', 'roadmap', 'profile'].includes(component)) {
          if (!scores[component][vertical] || new Date(run.completedAt) > new Date(scores[component][vertical].date)) {
            scores[component][vertical] = { score: run.score, date: run.completedAt };
          }
        }
      });
      
      // Extract just scores
      const finalScores = {};
      Object.keys(scores).forEach(comp => {
        finalScores[comp] = {};
        Object.keys(scores[comp]).forEach(vert => {
          finalScores[comp][vert] = scores[comp][vert].score;
        });
      });
      setComponentScores(finalScores);
      
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to load data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Run evaluation
  const runEvaluation = async (suite) => {
    const suiteId = `${suite.component}-${(suite.vertical || '').toLowerCase()}-prod`;
    
    try {
      setRunningTests(prev => ({ ...prev, [suite.id]: true }));
      setSuccessMessage(`Evaluation "${suite.name}" started! Refreshing...`);
      
      await adminApi.post('/api/admin/evaluations/run', { suiteId });

      
      // Poll for completion
      setTimeout(() => loadData(), 3000);
      setTimeout(() => loadData(), 10000);
      setTimeout(() => loadData(), 30000);
      
    } catch (err) {
      setError(`Failed to run evaluation: ${err.message}`);
    } finally {
      setRunningTests(prev => ({ ...prev, [suite.id]: false }));
    }
  };

  // Get last run for a suite
  const getLastRunForSuite = (suite) => {
    const component = suite.component?.toLowerCase();
    const vertical = (suite.vertical || '').toLowerCase();
    const match = recentRuns.find(r => {
      const runComponent = extractComponentName(r.suiteName || '').toLowerCase();
      const runVertical = (r.vertical || '').toLowerCase();
      return runComponent === component && runVertical === vertical;
    });
    return match?.completedAt;
  };

  // Filter suites by component
  const skillbotSuites = suites.filter(s => s.component === 'skillbot');
  const roadmapSuites = suites.filter(s => s.component === 'roadmap');
  const profileSuites = suites.filter(s => s.component === 'profile');

  // Calculate overall scores
  const calculateOverall = (componentKey) => {
    const scores = Object.values(componentScores[componentKey] || {});
    if (scores.length === 0) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  };

  // Get running evaluations list
  const runningList = Object.entries(runningTests)
    .filter(([_, running]) => running)
    .map(([id]) => {
      const suite = suites.find(s => s.id === id);
      return suite ? `${suite.component} ${suite.vertical}` : id;
    });

  if (loading && recentRuns.length === 0) {
    return (
      <AdminLayout>
        <div className="eval-dashboard loading">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading evaluation dashboard...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="eval-dashboard">
        {/* Sub-header */}
        <div className="eval-sub-header">
          <div className="sub-header-left">
            <h2>🎯 AI Evaluation Dashboard</h2>
            <p className="page-subtitle">Quality Assessment & LLM Judge Analysis</p>
          </div>
          <div className="sub-header-right">
            <span className="last-updated">Last updated: {formatDate(lastRefresh)}</span>
            <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={loading}>
              🔄 Refresh
            </button>
            <button className="btn btn-outline btn-sm" onClick={() => navigate('/admin')}>
              ← Back
            </button>
          </div>
        </div>

        {/* Active Evaluations Bar */}
        {runningList.length > 0 && (
          <div className="active-evaluations-bar">
            <span className="running-icon">🔄</span>
            <span className="running-text">
              Running {runningList.length} evaluation{runningList.length > 1 ? 's' : ''}: {runningList.join(' • ')}
            </span>
          </div>
        )}

        {/* Messages */}
        {error && <div className="message error">{error}</div>}
        {successMessage && <div className="message success">{successMessage}</div>}

        {/* Score Cards */}
        <div className="score-cards">
          <div className="score-card">
            <div className="card-label">🤖 SkillBot</div>
            <div className="vertical-scores">
              {['wms', 'iseries', 'civil', 'stitching', 'platform'].map(v => (
                <div key={v} className="vertical-score-item">
                  <span className="vs-label">{formatVertical(v)}</span>
                  <span className={`vs-value ${getScoreColor(componentScores.skillbot?.[v])}`}>
                    {componentScores.skillbot?.[v]?.toFixed(0) || '--'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="score-card">
            <div className="card-label">🗺️ Roadmap</div>
            <div className="vertical-scores">
              {['wms', 'iseries', 'civil', 'stitching', 'platform'].map(v => (
                <div key={v} className="vertical-score-item">
                  <span className="vs-label">{formatVertical(v)}</span>
                  <span className={`vs-value ${getScoreColor(componentScores.roadmap?.[v])}`}>
                    {componentScores.roadmap?.[v]?.toFixed(0) || '--'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="score-card">
            <div className="card-label">👤 Profile</div>
            <div className="vertical-scores">
              {['wms', 'iseries', 'civil', 'stitching', 'platform'].map(v => (
                <div key={v} className="vertical-score-item">
                  <span className="vs-label">{formatVertical(v)}</span>
                  <span className={`vs-value ${getScoreColor(componentScores.profile?.[v])}`}>
                    {componentScores.profile?.[v]?.toFixed(0) || '--'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* SkillBot Section */}
        <div className="eval-section">
          <div className="section-header">
            <h2>🤖 SkillBot (Chat)</h2>
            <p className="section-subtitle">Test AI chat responses using LLM Judge</p>
          </div>
          <div className="suites-grid">
            {skillbotSuites.map(suite => (
              <SuiteCard
                key={suite.id}
                suite={suite}
                onRunTest={runEvaluation}
                isRunning={!!runningTests[suite.id]}
                latestScore={componentScores.skillbot?.[suite.vertical?.toLowerCase()]}
                latestRun={getLastRunForSuite(suite)}
              />
            ))}
          </div>
        </div>

        {/* Roadmap Section */}
        <div className="eval-section">
          <div className="section-header">
            <h2>🗺️ Roadmap Generation</h2>
            <p className="section-subtitle">Test learning roadmap generation quality</p>
          </div>
          <div className="suites-grid">
            {roadmapSuites.map(suite => (
              <SuiteCard
                key={suite.id}
                suite={suite}
                onRunTest={runEvaluation}
                isRunning={!!runningTests[suite.id]}
                latestScore={componentScores.roadmap?.[suite.vertical?.toLowerCase()]}
                latestRun={getLastRunForSuite(suite)}
              />
            ))}
          </div>
        </div>

        {/* Profile Section */}
        <div className="eval-section">
          <div className="section-header">
            <h2>👤 Profile Analysis</h2>
            <p className="section-subtitle">Test resume/profile analysis accuracy</p>
          </div>
          <div className="suites-grid">
            {profileSuites.map(suite => (
              <SuiteCard
                key={suite.id}
                suite={suite}
                onRunTest={runEvaluation}
                isRunning={!!runningTests[suite.id]}
                latestScore={componentScores.profile?.[suite.vertical?.toLowerCase()]}
                latestRun={getLastRunForSuite(suite)}
              />
            ))}
          </div>
        </div>

        {/* Recent Runs */}
        <div className="eval-section">
          <div className="section-header">
            <h2>📊 Recent Evaluation Runs</h2>
            <p className="section-subtitle">Click a row to view detailed LLM Judge analysis</p>
          </div>
          {recentRuns.length === 0 ? (
            <div className="empty-state">
              <p>No evaluation runs yet. Run an evaluation to see results here.</p>
            </div>
          ) : (
            <div className="runs-table-container">
              <table className="runs-table">
                <thead>
                  <tr>
                    <th>Suite</th>
                    <th>Vertical</th>
                    <th>Status</th>
                    <th>Score</th>
                    <th>Pass Rate</th>
                    <th>Evaluator</th>
                    <th>Duration</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {recentRuns.map((run, idx) => {
                    const componentName = extractComponentName(run.suiteName || run.suite || 'Historical');
                    const displayVertical = formatVertical(run.vertical) || 'All';
                    const displayPassRate = formatPassRate(run.passed, run.total);
                    const isFailed = (run.status || '').toLowerCase() === 'failed';
                    
                    return (
                      <tr 
                        key={run._id || run.id || idx} 
                        className={`run-row clickable ${isFailed ? 'run-failed' : ''}`}
                        onClick={() => setSelectedRun(run)}
                      >
                        <td className="run-suite">{componentName}</td>
                        <td className="run-vertical">
                          <span className={`vertical-badge vertical-${(run.vertical || '').toLowerCase()}`}>
                            {displayVertical}
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge status-${(run.status || 'completed').toLowerCase()}`}>
                            {isFailed ? 'FAILED' : (run.status || 'Completed')}
                          </span>
                        </td>
                        <td className={`run-score ${isFailed ? 'score-failed' : getScoreColor(run.score)}`}>
                          {isFailed ? '❌ --' : `${getScoreEmoji(run.score)} ${run.score?.toFixed(1) || '--'}`}
                        </td>
                        <td className="run-passrate">{isFailed ? '--' : displayPassRate}</td>
                        <td className="run-evaluator">
                          <span className={`evaluator-tag ${run.evaluatedBy === 'llm-judge' ? 'llm' : 'heuristics'}`}>
                            {run.evaluatedBy === 'llm-judge' ? '🤖 LLM' : '📏 Heuristics'}
                          </span>
                        </td>
                        <td className="run-duration">{formatDuration(run.duration)}</td>
                        <td className="run-date">{formatDate(run.completedAt || run.createdAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Trend Charts */}
        <div className="eval-section">
          <div className="section-header">
            <h2>📈 Score Trends</h2>
            <p className="section-subtitle">Track evaluation scores over time</p>
          </div>
          <div className="trend-charts-container">
            {(() => {
              const extractVertical = (run) => {
                const directVertical = run.vertical || '';
                if (directVertical && !['mixed', 'all', 'overall'].includes(directVertical.toLowerCase())) {
                  const v = directVertical.toLowerCase();
                  if (v === 'wms') return 'WMS';
                  if (v === 'iseries') return 'iSeries';
                  if (v === 'civil') return 'Civil';
                  if (v === 'stitching') return 'Stitching';
                  return directVertical;
                }
                return null;
              };
              
              const buildTrendData = () => {
                const grouped = {};
                if (!recentRuns || recentRuns.length === 0) return grouped;
                
                const sortedRuns = [...recentRuns].sort((a, b) => 
                  new Date(a.completedAt) - new Date(b.completedAt)
                );
                
                sortedRuns.forEach(run => {
                  const score = run.score ?? run.overallScore;
                  if (score === null || score === undefined || isNaN(score)) return;
                  
                  const vertical = extractVertical(run);
                  if (!vertical) return;
                  
                  if (!grouped[vertical]) grouped[vertical] = [];
                  grouped[vertical].push({ date: run.completedAt, score, suite: run.suiteName });
                });
                
                return grouped;
              };
              
              const chartData = buildTrendData();
              
              if (Object.keys(chartData).length === 0) {
                return (
                  <div className="trend-chart-empty">
                    <p>📊 No trend data available yet. Run evaluations to see score history.</p>
                  </div>
                );
              }
              
              return <MultiTrendChart data={chartData} />;
            })()}
          </div>
        </div>

        {/* Evaluators Info */}
        <div className="eval-section">
          <div className="section-header">
            <h2>🔍 Evaluators</h2>
          </div>
          <div className="evaluators-grid">
            {evaluators.map(evaluator => (
              <div key={evaluator.id} className="evaluator-card">
                <div className="evaluator-header">
                  <span className="evaluator-name">{evaluator.name}</span>
                  <span className="evaluator-weight">{evaluator.weight}%</span>
                </div>
                <p className="evaluator-desc">{evaluator.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Result Detail Modal */}
        {selectedRun && (
          <TestResultModal 
            run={selectedRun} 
            onClose={() => setSelectedRun(null)} 
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default EvaluationDashboard;
